/**
 * Mitarbeiter: wer Trinkgeld bekommt, unter welcher Stempeluhr-ID, auf welches Konto.
 *
 * Oben steht, wer Trinkgeld hat und keine IBAN — das hält eine Auszahlung auf,
 * und so sieht auch ein neuer Name aus dem Stundenimport aus. Die volle IBAN
 * steht nur auf Knopfdruck auf dem Bildschirm, verschwindet nach einer halben
 * Minute wieder, und jede Anzeige steht im Audit-Log. Die Liste zeigt nur die
 * letzten vier Stellen.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { euro } from "../../lib/format";
import {
  FILTERS,
  defaultFilter,
  formatIban,
  fullName,
  matchesFilter,
  matchesSearch,
  sortEmployees,
  type EmployeeDto,
  type EmployeeFilter,
} from "../../lib/employees";
import { Amount, Empty, ErrorBox, Info, Section, Spinner } from "../../components/ui";
import { NewEmployees, emptyRow } from "../../components/NewEmployees";

/** So lange steht eine aufgedeckte IBAN auf dem Bildschirm. */
const REVEAL_SECONDS = 30;

export function Employees({
  initialFilter,
  initialSearch = "",
}: {
  initialFilter?: EmployeeFilter;
  initialSearch?: string;
}) {
  const queryClient = useQueryClient();
  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.get<{ employees: EmployeeDto[] }>("/employees"),
  });
  const list = employees.data?.employees ?? [];

  const [filter, setFilter] = useState<EmployeeFilter | null>(initialFilter ?? null);
  const [search, setSearch] = useState(initialSearch);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [arming, setArming] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ id: string; iban: string } | null>(null);

  // Aufgedeckte IBAN nach einer halben Minute wieder verdecken.
  useEffect(() => {
    if (!revealed) return;
    const t = setTimeout(() => setRevealed(null), REVEAL_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [revealed]);

  const reveal = useMutation({
    mutationFn: (id: string) => api.get<{ employeeId: string; iban: string }>(`/employees/${id}/iban`),
    onSuccess: (res) => setRevealed({ id: res.employeeId, iban: res.iban }),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.patch(`/employees/${id}`, { active }),
    onSuccess: async () => {
      setArming(null);
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  // Solange noch nichts geladen ist, steht der Filter nicht fest.
  const effectiveFilter = filter ?? defaultFilter(list);
  const counts = useMemo(
    () =>
      Object.fromEntries(FILTERS.map((f) => [f.value, list.filter((e) => matchesFilter(e, f.value)).length])) as Record<
        EmployeeFilter,
        number
      >,
    [list],
  );
  // Eine Suche geht über alle, nicht nur über den gewählten Filter: wer einen
  // Namen tippt, will die Person finden, egal wo sie steht.
  const shown = sortEmployees(
    list.filter((e) => (search.trim() ? matchesSearch(e, search) : matchesFilter(e, effectiveFilter))),
  );

  if (employees.isLoading) return <Spinner />;
  if (employees.error) return <ErrorBox error={employees.error} />;

  const wartend = list.filter((e) => e.ibanStatus === "fehlt_wartet");

  return (
    <Section
      label="Wer Trinkgeld bekommt"
      title="Mitarbeitende"
      actions={
        adding ? null : (
          <button className="btn-primary" onClick={() => setAdding(true)}>
            Person anlegen
          </button>
        )
      }
    >
      {adding ? (
        <div className="mb-6">
          <NewEmployees
            initial={[emptyRow()]}
            submitLabel="Anlegen"
            onCancel={() => setAdding(false)}
            onCreated={() => setAdding(false)}
          />
        </div>
      ) : null}

      {wartend.length > 0 ? (
        <div className="card mb-6 border-alert/40 p-4 text-sm">
          <div className="mb-1 font-medium">
            {wartend.length === 1
              ? "Eine Person bekommt Trinkgeld, aber es fehlt die IBAN."
              : `${wartend.length} Personen bekommen Trinkgeld, aber es fehlt die IBAN.`}
          </div>
          <p className="text-muted">
            Meist sind das neue Namen aus dem Stundenimport. Solange die IBAN fehlt, lässt sich die
            Auszahlung nicht zur Überweisung exportieren. Die IBAN steht auf dem Personalbogen oder in der
            Lohnabrechnung.
          </p>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          className="field w-64"
          type="search"
          placeholder="Name, PersNr oder Stempeluhr-ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {FILTERS.map((f) => (
          <button
            key={f.value}
            className={!search.trim() && effectiveFilter === f.value ? "btn-primary" : "btn-ghost"}
            onClick={() => {
              setSearch("");
              setFilter(f.value);
            }}
          >
            {f.label} <span className="ml-1 font-mono text-xs opacity-70">{counts[f.value]}</span>
          </button>
        ))}
      </div>

      {reveal.error ? (
        <div className="mb-4">
          <ErrorBox error={reveal.error} />
        </div>
      ) : null}
      {setActive.error ? (
        <div className="mb-4">
          <ErrorBox error={setActive.error} />
        </div>
      ) : null}

      {shown.length === 0 ? (
        <Empty>
          {search.trim()
            ? "Niemand gefunden."
            : effectiveFilter === "wartet"
              ? "Alle, die Trinkgeld bekommen, haben eine IBAN."
              : "Hier steht niemand."}
        </Empty>
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th className="w-20">PersNr</th>
                <th className="w-28">
                  Stempeluhr
                  <Info text="Steht statt des Namens auf dem Aushang." />
                </th>
                <th>
                  IBAN
                  <Info
                    text={`„Anzeigen“ zeigt die volle IBAN für ${REVEAL_SECONDS} Sekunden. Jede Anzeige steht mit Namen und Uhrzeit im Audit-Log.`}
                  />
                </th>
                <th className="w-36 text-right">
                  offen
                  <Info text="Trinkgeld aus Tagen, die noch nicht ausgezahlt sind." />
                </th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => {
                const isRevealed = revealed?.id === e.id;
                const isEditing = editing === e.id;
                return (
                  <Fragment key={e.id}>
                    <tr className={e.active ? "" : "opacity-50"}>
                      <td>
                        <div>{fullName(e) || "–"}</div>
                        {e.hasIban && e.accountHolder && e.accountHolder !== fullName(e) ? (
                          <div className="text-xs text-muted" title="So steht der Name in der Überweisung">
                            Konto: {e.accountHolder}
                          </div>
                        ) : null}
                        {!e.active ? <span className="chip-muted">ausgeschieden</span> : null}
                      </td>
                      <td className="font-mono text-xs text-muted">{e.personalNr}</td>
                      <td className="font-mono text-xs text-muted">{e.timeClockId ?? "–"}</td>
                      <td>
                        {e.hasIban ? (
                          isRevealed ? (
                            <span className="inline-flex flex-wrap items-center gap-2">
                              <span className="whitespace-nowrap font-mono text-sm">{formatIban(revealed.iban)}</span>
                              <button
                                className="btn-ghost"
                                onClick={() => void navigator.clipboard?.writeText(revealed.iban)}
                              >
                                Kopieren
                              </button>
                              <button className="btn-ghost" onClick={() => setRevealed(null)}>
                                Verdecken
                              </button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="font-mono text-xs text-muted">•••• {e.ibanLast4 ?? "····"}</span>
                              <button
                                className="btn-ghost"
                                disabled={reveal.isPending}
                                onClick={() => reveal.mutate(e.id)}
                              >
                                Anzeigen
                              </button>
                            </span>
                          )
                        ) : e.ibanStatus === "fehlt_wartet" ? (
                          <span className="chip-alert">fehlt</span>
                        ) : (
                          <span className="chip-muted">keine</span>
                        )}
                      </td>
                      <td className="text-right">
                        {e.offenesTrinkgeld ? <Amount value={euro(e.offenesTrinkgeld)} /> : <span className="text-muted">–</span>}
                      </td>
                      <td className="text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          {e.active ? (
                            <button
                              className={e.ibanStatus === "fehlt_wartet" ? "btn-primary" : "btn-ghost"}
                              onClick={() => setEditing(isEditing ? null : e.id)}
                            >
                              {e.hasIban ? "Bankdaten ändern" : "IBAN eintragen"}
                            </button>
                          ) : null}
                          {arming === e.id ? (
                            <span className="inline-flex gap-1">
                              <button
                                className={e.active ? "btn-danger" : "btn-primary"}
                                onClick={() => setActive.mutate({ id: e.id, active: !e.active })}
                              >
                                {e.active ? "Ja, ausgeschieden" : "Ja, wieder aktiv"}
                              </button>
                              <button className="btn-ghost" onClick={() => setArming(null)}>
                                Nein
                              </button>
                            </span>
                          ) : (
                            <button className="btn-ghost" onClick={() => setArming(e.id)}>
                              {e.active ? "Ausgeschieden" : "Wieder aktiv"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isEditing ? (
                      <tr>
                        <td colSpan={6} className="bg-surface">
                          <IbanForm employee={e} onDone={() => setEditing(null)} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-muted">
        Ausgeschieden statt löschen: frühere Auszahlungen und das Audit-Log verweisen weiter auf die
        Person. Neue Namen aus dem Stundenimport lassen sich direkt beim Import anlegen.
      </p>
    </Section>
  );
}

function IbanForm({ employee, onDone }: { employee: EmployeeDto; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [iban, setIban] = useState("");
  const [reason, setReason] = useState("");
  const [holder, setHolder] = useState(employee.accountHolder ?? "");
  // Eine vorhandene IBAN zu ersetzen braucht einen Grund; die erste nicht.
  const changesIban = iban.trim().length > 0;
  const needsReason = employee.hasIban && changesIban;
  const changesHolder = holder.trim() !== (employee.accountHolder ?? "");

  const save = useMutation({
    mutationFn: async () => {
      if (changesIban) {
        await api.put(`/employees/${employee.id}/iban`, {
          iban: iban.trim(),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
      }
      if (changesHolder) {
        await api.put(`/employees/${employee.id}/account-holder`, { accountHolder: holder.trim() || null });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
      onDone();
    },
  });

  const ready =
    (changesIban || (employee.hasIban && changesHolder)) &&
    (!changesIban || iban.trim().length >= 15) &&
    (!needsReason || reason.trim().length >= 3);

  return (
    <form
      className="grid gap-3 py-2 md:grid-cols-[1fr_1fr_1fr_auto]"
      onSubmit={(ev) => {
        ev.preventDefault();
        save.mutate();
      }}
    >
      <label className="block">
        <span className="label mb-1 block">
          {employee.hasIban ? "Neue IBAN (leer: bleibt)" : "IBAN"} für {fullName(employee)}
        </span>
        <input
          className="field font-mono"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder="DE.."
          value={iban}
          onChange={(ev) => setIban(ev.target.value)}
        />
      </label>
      <label className="block">
        <span className="label mb-1 block">
          Name am Konto
          <Info text="So steht der Name in der Überweisung. Die empfangende Bank gleicht ihn mit dem Namen ab, auf den das Konto läuft. Leer lassen, wenn er dem Namen oben entspricht." />
        </span>
        <input
          className="field"
          value={holder}
          placeholder={fullName(employee).replace(/\s*\([^)]*\)\s*$/, "")}
          onChange={(ev) => setHolder(ev.target.value)}
        />
      </label>
      <label className="block">
        <span className="label mb-1 block">Grund{needsReason ? "" : " (freiwillig)"}</span>
        <input
          className="field"
          placeholder={needsReason ? "z. B. neues Konto laut Personalbogen" : ""}
          value={reason}
          onChange={(ev) => setReason(ev.target.value)}
        />
        {needsReason ? (
          <span className="mt-1 block text-xs text-muted">
            Eine vorhandene IBAN wird nur mit Grund ersetzt. So tauscht niemand still ein Konto.
          </span>
        ) : null}
      </label>
      <div className="flex items-end gap-2">
        <button className="btn-primary" type="submit" disabled={save.isPending || !ready}>
          {save.isPending ? "Speichert …" : "Speichern"}
        </button>
        <button className="btn-ghost" type="button" onClick={onDone}>
          Abbrechen
        </button>
      </div>
      {save.error ? (
        <div className="md:col-span-4">
          <ErrorBox error={save.error} />
        </div>
      ) : null}
    </form>
  );
}
